import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiUsers, FiPlus, FiDollarSign, FiUser, FiChevronLeft, FiChevronRight, FiMenu, FiSettings } = FiIcons;

const Sidebar = () => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Set sidebar to collapsed state by default on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    // Initial check on mount
    handleResize();

    // Listen for window resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check if current user is Peter (case-insensitive)
  const isPeter = user?.name?.toLowerCase().includes('peter') || user?.username?.toLowerCase().includes('peter');

  const navItems = [
    { path: '/', icon: FiHome, label: 'Dashboard' },
    { path: '/members', icon: FiUsers, label: 'Members' },
    { path: '/add-member', icon: FiPlus, label: 'Add Member' },
    { path: '/manage-coaches', icon: FiUser, label: 'Coaches' }
  ];

  // Only add App Settings if user is Peter
  if (isPeter) {
    navItems.push({ path: '/settings', icon: FiSettings, label: 'App Settings' });
  }

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <motion.div
      initial={false}
      animate={{ width: isCollapsed ? '64px' : '256px' }}
      className={`bg-primary-700 text-white flex flex-col h-full transition-all duration-300 relative z-20`}
    >
      <div className={`p-4 flex ${isCollapsed ? 'justify-center' : 'px-6'}`}>
        <div className={`flex items-center ${isCollapsed ? '' : 'space-x-3'}`}>
          <SafeIcon icon={FiDollarSign} className="w-8 h-8 text-accent-500" />
          {!isCollapsed && (
            <div>
              <h2 className="text-xl font-bold">BPA</h2>
              <p className="text-primary-200 text-sm">Success Center</p>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-2">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  ` flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3 px-4'} py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-primary-200 hover:text-white hover:bg-primary-600'
                  } `
                }
              >
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                {!isCollapsed && <span className="font-medium">{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Toggle button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-16 bg-primary-600 hover:bg-primary-500 text-white rounded-full p-1 shadow-md"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <SafeIcon icon={isCollapsed ? FiChevronRight : FiChevronLeft} className="w-4 h-4" />
      </button>

      {/* Mobile menu button - only visible on very small screens */}
      <button
        onClick={toggleSidebar}
        className="md:hidden fixed z-30 bottom-4 right-4 bg-primary-600 text-white rounded-full p-3 shadow-lg"
        aria-label="Toggle menu"
      >
        <SafeIcon icon={FiMenu} className="w-6 h-6" />
      </button>

      <div className={`p-2 border-t border-primary-600 ${isCollapsed ? 'text-center' : 'p-4'}`}>
        {!isCollapsed && (
          <p className="text-xs text-primary-300 text-center">
            Digital Marketing Coach Platform
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default Sidebar;